import React, { useEffect, useState, useRef } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  Menu, X, Instagram, Facebook, Youtube, Mail, Phone, MapPin,
  ArrowRight, ArrowUpRight, Play, ChevronDown, Sparkles, Send, Share2,
  Copy, MessageCircle, Check, Music4, Mic2, Volume2, Truck, Building2,
  Megaphone, Lightbulb, ShieldCheck, Star, CalendarDays
} from "lucide-react";

// Admin imports
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  LoginPage, AdminLayout, DashboardPage,
  NewsPage, GalleryPage, ArtistsPage, ContactsPage
} from "./pages/admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_ROUND = "/logo-round.jpg";
const LOGO_FULL = "/logo-full.jpg";

const CONTACT = {
  phoneDisplay: "(71) 99708-1013",
  phoneTel: "+5571997081013",
  whatsapp: "5571997081013",
  email: "ritmoproducoes@gmail.com",
  city: "Lauro de Freitas / BA",
  instagram: "https://www.instagram.com/ritmoproducoes/",
  facebook: "https://www.facebook.com/ritmoproducoes",
  youtube: "https://www.youtube.com/@ritmoproducoeseventos",
};

// Helper: resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// slug helper
const slugify = (name = "") =>
  name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// ---------------- Protected Route ----------------
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#060A14] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#22D3EE] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin" replace />;
  return children;
};

// ---------------- Navigation ----------------
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { name: "Sobre", href: "#sobre" },
    { name: "Serviços", href: "#servicos" },
    { name: "Processo", href: "#processo" },
    { name: "Artistas", href: "#artistas" },
    { name: "Notícias", href: "#noticias" },
    { name: "Galeria", href: "#galeria" },
    { name: "Contato", href: "#contato" },
  ];

  return (
    <motion.nav
      initial={{ y: -90 }} animate={{ y: 0 }} transition={{ duration: 0.6 }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "nav-glass py-3" : "bg-transparent py-5"}`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="#inicio" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_ROUND} alt="Ritmo" className="h-11 w-11 rounded-full ring-1 ring-[#22D3EE]/40" />
          <span className="font-display font-bold text-lg tracking-tight hidden sm:block">
            RITMO <span className="text-[#22D3EE]">/</span> <span className="text-gray-400 font-normal text-sm">Produções</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <a key={item.name} href={item.href}
              className="text-sm font-medium text-gray-300 hover:text-white transition-colors relative group"
              data-testid={`nav-${item.name.toLowerCase()}`}>
              {item.name}
              <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-gradient-to-r from-[#22D3EE] to-[#0A84FF] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/admin" className="text-sm font-medium text-gray-400 hover:text-[#22D3EE] transition-colors" data-testid="nav-entrar">
            Entrar
          </Link>
          <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Olá! Gostaria de um orçamento com a Ritmo Produções.")}`}
            target="_blank" rel="noopener noreferrer" className="btn-primary py-2.5 px-5 text-sm" data-testid="nav-cta">
            Orçamento <ArrowUpRight size={16} />
          </a>
        </div>

        <button className="lg:hidden text-white p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
          {isOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden nav-glass border-t border-white/10 mt-3" data-testid="mobile-menu">
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <a key={item.name} href={item.href} onClick={() => setIsOpen(false)}
                  className="block text-lg font-medium text-gray-200 hover:text-[#22D3EE]">{item.name}</a>
              ))}
              <Link to="/admin" onClick={() => setIsOpen(false)} className="block text-lg font-medium text-[#22D3EE]">Entrar</Link>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-primary w-full">Solicitar Orçamento</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// ---------------- Hero ----------------
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const stats = [
    { n: "+50", l: "Eventos" },
    { n: "+20", l: "Artistas" },
    { n: "+100K", l: "Público" },
    { n: "+30", l: "Cidades" },
  ];

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      {/* Aurora background */}
      <div className="aurora w-[520px] h-[520px] bg-[#0A84FF]/40 -top-32 -left-24" />
      <div className="aurora w-[460px] h-[460px] bg-[#22D3EE]/30 top-1/3 right-0 animate-float" />
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#060A14]/40 to-[#060A14]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
        <motion.div style={{ y }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-7">
            <span className="w-2 h-2 rounded-full bg-[#22D3EE] animate-pulse" />
            <span className="text-xs font-medium tracking-widest uppercase text-gray-300">Produções & Eventos • Bahia</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-bold text-[3.2rem] sm:text-7xl lg:text-8xl leading-[0.92] uppercase" data-testid="hero-title">
            No <span className="gradient-text">Ritmo</span><br />do seu evento
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-lg text-gray-300 max-w-xl mt-7 leading-relaxed" data-testid="hero-description">
            Curadoria artística, produção de shows e estrutura completa de palco, som e luz.
            Transformamos ideias em espetáculos inesquecíveis.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 mt-9">
            <a href="#artistas" className="btn-primary" data-testid="hero-cta-artists">Nossos Artistas <ArrowRight size={18} /></a>
            <a href="#contato" className="btn-ghost" data-testid="hero-cta-about">Fazer Orçamento</a>
          </motion.div>

          <div className="grid grid-cols-4 gap-4 mt-14 max-w-xl">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }}>
                <div className="font-display text-2xl sm:text-3xl font-bold gradient-text">{s.n}</div>
                <div className="text-xs text-gray-500 mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Logo showcase */}
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
          className="lg:col-span-5 relative hidden lg:flex justify-center items-center">
          <div className="absolute w-80 h-80 rounded-full border border-[#22D3EE]/20 animate-spin-slow" />
          <div className="absolute w-[26rem] h-[26rem] rounded-full border border-[#0A84FF]/10" />
          <div className="absolute w-72 h-72 rounded-full bg-[#0A84FF]/20 blur-3xl" />
          <img src={LOGO_ROUND} alt="Ritmo Produções" className="relative w-72 h-72 rounded-full object-cover ring-1 ring-white/10 animate-float shadow-2xl" />
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ChevronDown className="text-white/40" size={30} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// ---------------- Marquee ----------------
const MarqueeSection = () => {
  const items = ["SHOWS", "FESTIVAIS", "EVENTOS CORPORATIVOS", "CARNAVAL", "SÃO JOÃO", "RÉVEILLON", "FORMATURAS", "CASAMENTOS"];
  const row = [...items, ...items];
  return (
    <div className="border-y border-white/10 bg-[#0A1020] py-4 overflow-hidden" data-testid="marquee-section">
      <div className="marquee-track">
        {row.map((it, i) => (
          <span key={i} className="font-display font-semibold text-sm tracking-[0.2em] uppercase px-6 flex items-center gap-3 text-gray-300">
            {it} <Sparkles size={14} className="text-[#22D3EE]" />
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------- About ----------------
const AboutSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <section id="sobre" className="py-24 md:py-32 relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <span className="kicker">Quem Somos</span>
            <h2 className="section-title mt-4 mb-7" data-testid="about-title">
              Somos curadores de <span className="gradient-text">experiências</span>
            </h2>
            <div className="space-y-5 text-gray-300 text-[1.05rem] leading-relaxed">
              <p>
                Nascemos da paixão pela música e pela cultura da Bahia. Com sede em{" "}
                <strong className="text-white">Lauro de Freitas/BA</strong>, a{" "}
                <strong className="text-[#22D3EE]">Ritmo Produções e Eventos</strong> se consolidou como uma
                força motriz do entretenimento no Nordeste.
              </p>
              <p>
                Oferecemos uma <strong className="text-white">gestão 360°</strong> — da curadoria artística à
                logística de palco, som e luz — com soluções personalizadas que garantem segurança,
                pontualidade e sucesso de público.
              </p>
              <p className="text-[#7DD3FC]">
                Acreditamos no poder da arte para unir, celebrar e transformar. Construa o próximo grande espetáculo conosco.
              </p>
            </div>
            <a href="#servicos" className="btn-ghost mt-8">Ver serviços <ArrowRight size={16} /></a>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative">
            <div className="card-surface rounded-3xl p-8 relative overflow-hidden">
              <div className="aurora w-64 h-64 bg-[#0A84FF]/30 -top-16 -right-10" />
              <div className="relative grid grid-cols-2 gap-5">
                {[
                  { n: "360°", l: "Gestão completa de eventos" },
                  { n: "10+", l: "Anos de experiência" },
                  { n: "24/7", l: "Suporte técnico dedicado" },
                  { n: "100%", l: "Compromisso com a excelência" },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="font-display text-3xl font-bold gradient-text">{s.n}</div>
                    <p className="text-sm text-gray-400 mt-2">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="relative mt-6 flex items-center gap-3 rounded-2xl border border-[#22D3EE]/20 bg-[#22D3EE]/5 p-4">
                <ShieldCheck className="text-[#22D3EE]" size={22} />
                <p className="text-sm text-gray-300">Assessoria completa para <strong className="text-white">prefeituras</strong> — Lei 14.133/2021.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ---------------- Services (bento) ----------------
const ServicesSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const services = [
    { icon: Music4, title: "Gestão de Carreira", desc: "Desenvolvimento completo da carreira do artista, da identidade visual à estratégia de mercado.", big: true },
    { icon: Mic2, title: "Produção de Shows", desc: "Planejamento e execução de eventos de todos os portes com equipe especializada." },
    { icon: Volume2, title: "Som & Iluminação", desc: "Estrutura completa de áudio e luz de última geração." },
    { icon: Truck, title: "Logística", desc: "Transporte, hospedagem, alimentação, segurança e documentação." },
    { icon: Building2, title: "Prefeituras", desc: "Suporte para contratação por inexigibilidade, em conformidade com a Lei 14.133/2021.", big: true },
    { icon: Megaphone, title: "Marketing", desc: "Divulgação em redes sociais, assessoria de imprensa e material promocional." },
  ];
  return (
    <section id="servicos" className="py-24 md:py-32 bg-[#080D1A] relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          className="max-w-2xl mb-14">
          <span className="kicker">O Que Fazemos</span>
          <h2 className="section-title mt-4" data-testid="services-title">Soluções <span className="gradient-text">completas</span></h2>
          <p className="text-gray-400 mt-4">Tudo que o seu evento precisa, do primeiro contato ao encerramento do palco.</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 auto-rows-fr">
          {services.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 26 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`card-surface tilt-card rounded-3xl p-7 group ${s.big ? "md:col-span-2" : ""}`}>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0A84FF] to-[#22D3EE] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <s.icon className="text-white" size={22} />
              </div>
              <h3 className="font-display font-semibold text-xl mb-2 group-hover:text-[#22D3EE] transition-colors">{s.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- Process ----------------
const ProcessSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const steps = [
    { icon: MessageCircle, t: "Briefing", d: "Entendemos sua visão, público e objetivos do evento." },
    { icon: Lightbulb, t: "Curadoria", d: "Selecionamos artistas e desenhamos a experiência ideal." },
    { icon: CalendarDays, t: "Produção", d: "Palco, som, luz e logística executados com precisão." },
    { icon: Star, t: "Espetáculo", d: "Sua ideia no ar, com público vibrando no ritmo certo." },
  ];
  return (
    <section id="processo" className="py-24 md:py-32 relative" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-16">
          <span className="kicker">Como Funciona</span>
          <h2 className="section-title mt-4" data-testid="process-title">Do briefing ao <span className="gradient-text">palco</span></h2>
        </motion.div>
        <div className="grid md:grid-cols-4 gap-6 relative">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 26 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.12 }}
              className="relative">
              <div className="card-surface rounded-3xl p-7 h-full">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <s.icon className="text-[#22D3EE]" size={22} />
                  </div>
                  <span className="font-display text-4xl font-bold text-white/10">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-display font-semibold text-lg mb-2">{s.t}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- Empty state ----------------
const EmptyState = ({ text }) => (
  <div className="col-span-full">
    <div className="card-surface rounded-3xl py-16 px-6 text-center border-dashed">
      <div className="w-14 h-14 mx-auto rounded-full bg-[#0A84FF]/10 flex items-center justify-center mb-4">
        <Sparkles className="text-[#22D3EE]" size={24} />
      </div>
      <p className="text-gray-400">{text}</p>
    </div>
  </div>
);

// ---------------- Artists ----------------
const ArtistsSection = ({ artists }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!artists || artists.length === 0) return;
    const slug = new URLSearchParams(window.location.search).get("artista");
    if (slug) {
      const a = artists.find((x) => slugify(x.name) === slug);
      if (a) setSelected(a);
    }
  }, [artists]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (selected) url.searchParams.set("artista", slugify(selected.name));
    else url.searchParams.delete("artista");
    window.history.replaceState({}, "", url.toString());
  }, [selected]);

  const buildShareUrl = (a) => `${window.location.origin}/api/share/${slugify(a.name)}`;
  const buildWhatsAppShareUrl = (a) =>
    `https://wa.me/?text=${encodeURIComponent(`Confira o perfil de ${a.name} no site da Ritmo Produções: ${buildShareUrl(a)}`)}`;

  const handleCopy = async (a) => {
    const link = buildShareUrl(a);
    try { await navigator.clipboard.writeText(link); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };

  return (
    <section id="artistas" className="py-24 md:py-32 bg-[#080D1A]" data-testid="artists-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="kicker">Nosso Elenco</span>
            <h2 className="section-title mt-4" data-testid="artists-title">Conheça nossos <span className="gradient-text">artistas</span></h2>
          </div>
          <p className="text-gray-400 max-w-sm">Talentos que fazem o público vibrar. Clique para ver a biografia e contratar.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {artists.length === 0 && <EmptyState text="Nosso elenco de artistas será revelado em breve. Fique ligado!" />}
          {artists.map((artist, index) => (
            <motion.div key={artist.id} initial={{ opacity: 0, y: 26 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.08 }}
              onClick={() => setSelected(artist)}
              className={`artist-card tilt-card group cursor-pointer border border-white/8 ${artist.featured ? "sm:col-span-2 sm:row-span-2" : ""}`}
              data-testid={`artist-card-${index}`}>
              <div className={`relative overflow-hidden ${artist.featured ? "h-[460px] sm:h-full min-h-[460px]" : "h-80"}`}>
                <img src={resolveImageUrl(artist.image)} alt={artist.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white text-[11px] font-bold uppercase tracking-wider mb-3">{artist.category}</span>
                  <h3 className={`font-display font-bold text-white ${artist.featured ? "text-3xl" : "text-xl"}`}>{artist.name}</h3>
                  <p className="text-[#22D3EE] text-sm mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Ver perfil <ArrowRight size={14} /></p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Artist modal */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
            onClick={() => setSelected(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }}
              className="w-full max-w-4xl my-auto bg-[#0A1020] rounded-3xl overflow-hidden border border-white/10 max-h-[95vh] sm:max-h-[90vh] flex flex-col md:grid md:grid-cols-2"
              onClick={(e) => e.stopPropagation()}>
              <div className="relative w-full h-72 sm:h-80 md:h-auto md:min-h-[520px] flex-shrink-0">
                <img src={resolveImageUrl(selected.image)} alt={selected.name} loading="eager" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r" />
                <button onClick={() => setSelected(null)} aria-label="Fechar" className="absolute top-3 right-3 w-10 h-10 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#0A84FF] transition-colors md:hidden z-10"><X size={20} /></button>
              </div>
              <div className="p-5 sm:p-6 md:p-8 relative overflow-y-auto flex-1">
                <button onClick={() => setSelected(null)} aria-label="Fechar" className="hidden md:flex absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full items-center justify-center text-white hover:bg-[#0A84FF] transition-colors z-10"><X size={20} /></button>
                <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white text-xs font-bold uppercase tracking-wider mb-4">{selected.category}</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-5 md:mb-6 leading-tight">{selected.name}</h2>

                <div className="mb-6 pb-6 border-b border-white/10">
                  <h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2"><Share2 size={16} />Compartilhar Perfil</h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <a href={buildWhatsAppShareUrl(selected)} target="_blank" rel="noopener noreferrer" data-testid={`artist-share-whatsapp-${selected.id}`}
                      className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1ebe57] rounded-lg text-white text-sm font-medium transition-colors"><MessageCircle size={18} />WhatsApp</a>
                    <button type="button" onClick={() => handleCopy(selected)} data-testid={`artist-share-copy-${selected.id}`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors ${copied ? "bg-emerald-600" : "bg-zinc-700 hover:bg-zinc-600"}`}>
                      {copied ? <Check size={18} /> : <Copy size={18} />}{copied ? "Link copiado!" : "Copiar link"}
                    </button>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-3">Contratação</h3>
                  <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center gap-2 px-4 py-2 bg-green-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity w-fit"><Phone size={18} />{CONTACT.phoneDisplay}</a>
                  <p className="text-gray-500 text-xs mt-2">Ritmo Produções e Eventos</p>
                </div>

                {selected.social_links && (selected.social_links.instagram || selected.social_links.facebook || selected.social_links.youtube) && (
                  <div className="mb-6">
                    <h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-3">Redes Sociais</h3>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {selected.social_links.instagram && (
                        <a href={selected.social_links.instagram.startsWith("http") ? selected.social_links.instagram : `https://www.instagram.com/${selected.social_links.instagram.replace("@", "").replace(/^\/+|\/+$/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`artist-social-instagram-${selected.id}`}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"><Instagram size={18} />Instagram</a>
                      )}
                      {selected.social_links.facebook && (
                        <a href={selected.social_links.facebook.startsWith("http") ? selected.social_links.facebook : `https://www.facebook.com/${selected.social_links.facebook.replace(/^\/+|\/+$/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`artist-social-facebook-${selected.id}`}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"><Facebook size={18} />Facebook</a>
                      )}
                      {selected.social_links.youtube && (
                        <a href={selected.social_links.youtube.startsWith("http") ? selected.social_links.youtube : `https://www.youtube.com/${selected.social_links.youtube.replace(/^\/+|\/+$/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`artist-social-youtube-${selected.id}`}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"><Youtube size={18} />YouTube</a>
                      )}
                    </div>
                  </div>
                )}

                {selected.description && (
                  <div className="mb-6">
                    <h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-3">Biografia</h3>
                    <p className="text-gray-300 leading-relaxed text-sm sm:text-base whitespace-pre-line">{selected.description}</p>
                  </div>
                )}

                <a href="#contato" onClick={() => setSelected(null)} className="btn-primary">Contratar {selected.name.split(" ")[0]}<ArrowRight size={18} /></a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ---------------- News ----------------
const NewsSection = ({ news }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [selected, setSelected] = useState(null);
  return (
    <section id="noticias" className="py-24 md:py-32" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="mb-14">
          <span className="kicker">Fique por Dentro</span>
          <h2 className="section-title mt-4" data-testid="news-title">Últimas <span className="gradient-text">notícias</span></h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {news.length === 0 && <EmptyState text="Novidades e bastidores dos nossos eventos aparecerão aqui em breve." />}
          {news.map((item, index) => (
            <motion.article key={item.id} initial={{ opacity: 0, y: 26 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.08 }}
              onClick={() => setSelected(item)} className="card-surface tilt-card rounded-3xl overflow-hidden group cursor-pointer" data-testid={`news-card-${index}`}>
              <div className="relative h-56 overflow-hidden">
                <img src={resolveImageUrl(item.image)} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute top-4 left-4"><span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white text-[11px] font-bold uppercase tracking-wider">{item.category}</span></div>
              </div>
              <div className="p-6">
                <h3 className="font-display font-semibold text-lg mb-2 line-clamp-2 group-hover:text-[#22D3EE] transition-colors">{item.title}</h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-4">{item.excerpt}</p>
                <span className="text-[#22D3EE] font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">Ler notícia <ArrowRight size={14} /></span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-start justify-center overflow-y-auto py-8" onClick={() => setSelected(null)}>
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} className="w-full max-w-4xl mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="bg-[#0A1020] rounded-3xl overflow-hidden border border-white/10">
                <div className="relative h-64 md:h-80">
                  <img src={resolveImageUrl(selected.image)} alt={selected.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] to-transparent" />
                  <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-[#0A84FF] transition-colors" data-testid="close-news-modal"><X size={20} /></button>
                  <div className="absolute bottom-4 left-6"><span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white text-xs font-bold uppercase tracking-wider">{selected.category}</span></div>
                </div>
                <div className="p-6 md:p-10">
                  <h1 className="font-display font-bold text-2xl md:text-3xl text-white mb-6">{selected.title}</h1>
                  <div className="prose prose-invert max-w-none">
                    {selected.content && selected.content.split("\n\n").map((p, idx) => (
                      <p key={idx} className="text-gray-300 text-base md:text-lg leading-relaxed mb-4 whitespace-pre-line">{p}</p>
                    ))}
                  </div>
                  {selected.source_url && (
                    <div className="mt-10 pt-8 border-t border-white/10">
                      <p className="text-gray-500 text-sm mb-4">Fonte: <span className="text-[#22D3EE]">{selected.source_name || "Fonte Externa"}</span></p>
                      <a href={selected.source_url} target="_blank" rel="noopener noreferrer" className="btn-primary">Ler matéria completa <ArrowRight size={18} /></a>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ---------------- Gallery ----------------
const GallerySection = ({ gallery }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [selected]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return m ? m[1] : null;
  };
  const isDirectVideo = (url) => !!url && (url.includes("/api/files/videos/") || url.includes("/uploads/videos/") || url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov"));

  return (
    <section id="galeria" className="py-24 md:py-32 bg-[#080D1A]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="kicker">Nossos Momentos</span>
            <h2 className="section-title mt-4" data-testid="gallery-title"><span className="gradient-text">Galeria</span></h2>
          </div>
          <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="gallery-instagram-cta"><Instagram size={18} />Seguir no Instagram</a>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[220px]">
          {gallery.length === 0 && <EmptyState text="As fotos e vídeos dos nossos eventos estarão aqui muito em breve." />}
          {gallery.map((item, index) => (
            <motion.div key={item.id} initial={{ opacity: 0, scale: 0.92 }} animate={inView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.5, delay: index * 0.04 }}
              onClick={() => setSelected(item)} className={`gallery-item cursor-pointer border border-white/8 ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`} data-testid={`gallery-item-${index}`}>
              <img src={resolveImageUrl(item.image)} alt={item.title} />
              {item.video_url && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"><Play size={26} className="text-white ml-1" fill="white" /></div>
                </div>
              )}
              <div className="gallery-overlay flex items-end p-4">
                <div>
                  <span className="text-[#22D3EE] text-xs font-medium uppercase tracking-wider">{item.category}</span>
                  <h4 className="text-white font-semibold mt-1">{item.title}</h4>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="bg-[#0A1020] rounded-3xl overflow-hidden max-w-5xl w-full my-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative bg-black">
                {selected.video_url ? (
                  getYouTubeId(selected.video_url) ? (
                    <div className="relative w-full h-full min-h-[300px] md:min-h-[500px]">
                      <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${getYouTubeId(selected.video_url)}?autoplay=1`} title={selected.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                  ) : isDirectVideo(selected.video_url) ? (
                    <video controls autoPlay className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]"><source src={selected.video_url} type="video/mp4" />Seu navegador não suporta vídeo.</video>
                  ) : (
                    <div className="relative">
                      <img src={resolveImageUrl(selected.image)} alt={selected.title} className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]" />
                      <a href={selected.video_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors">
                        <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"><Play size={34} className="text-white ml-1" fill="white" /></div>
                      </a>
                    </div>
                  )
                ) : (
                  <img src={resolveImageUrl(selected.image)} alt={selected.title} className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]" />
                )}
              </div>
              <div className="p-6 md:p-8 relative">
                <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"><X size={24} /></button>
                <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white text-xs font-bold uppercase tracking-wider mb-4">{selected.category}</span>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-6">{selected.title}</h2>
                {selected.event_name && (<div className="mb-6"><h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-2">Evento</h3><p className="text-gray-300">{selected.event_name}</p></div>)}
                {selected.description && (<div className="mb-6"><h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-2">Descrição</h3><p className="text-gray-300 leading-relaxed">{selected.description}</p></div>)}
                {selected.source_url && (
                  <div className="mb-6"><h3 className="text-[#22D3EE] font-bold text-sm uppercase tracking-wider mb-3">Fonte Original</h3>
                    <a href={selected.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors"><Instagram size={18} />Ver publicação original</a>
                  </div>
                )}
                {selected.video_url && !getYouTubeId(selected.video_url) && !isDirectVideo(selected.video_url) && (
                  <a href={selected.video_url} target="_blank" rel="noopener noreferrer" className="btn-primary w-full mt-4"><Play size={20} />Assistir vídeo <ArrowRight size={18} /></a>
                )}
                <p className="text-gray-600 text-sm mt-6">Ritmo Produções e Eventos</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ---------------- Contact ----------------
const ContactSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [artists, setArtists] = useState([]);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
    event_type: "", contract_type: "", contract_details: "", selected_artists: [], event_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/artists`).then((r) => setArtists(r.data)).catch((e) => console.error(e));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, { ...formData, selected_artists: formData.selected_artists.join(", ") });
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", subject: "", message: "", event_type: "", contract_type: "", contract_details: "", selected_artists: [], event_date: "" });
      setTimeout(() => setSuccess(false), 5000);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const toggleArtist = (name) => setFormData((prev) => ({
    ...prev,
    selected_artists: prev.selected_artists.includes(name) ? prev.selected_artists.filter((n) => n !== name) : [...prev.selected_artists, name],
  }));
  const detailsPlaceholder = () => ({
    orgao_publico: "Ex: Prefeitura, Secretaria de Cultura...",
    festa_cidade: "Ex: São João, Carnaval, Réveillon da cidade...",
    festival_publico: "Ex: Festival de Inverno, Micareta...",
    evento_corporativo: "Ex: Nome da empresa, tipo de evento...",
    festa_particular: "Ex: Aniversário, Casamento, Confraternização...",
  }[formData.contract_type] || "Descreva os detalhes da contratação...");

  const inputCls = "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#22D3EE] focus:ring-1 focus:ring-[#22D3EE] transition-colors";

  return (
    <section id="contato" className="py-24 md:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="grid lg:grid-cols-2 gap-14">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <span className="kicker">Entre em Contato</span>
            <h2 className="section-title mt-4 mb-6" data-testid="contact-title">Vamos criar seu <span className="gradient-text">evento</span></h2>
            <p className="text-gray-300 text-lg mb-10 leading-relaxed">
              Conte sua ideia e nossa equipe retorna com uma proposta sob medida. Do show intimista ao grande festival.
            </p>
            <div className="space-y-5">
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-2xl bg-[#22D3EE]/10 flex items-center justify-center"><Mail className="text-[#22D3EE]" size={20} /></div>
                <div><p className="text-gray-500 text-sm">Email</p><p className="text-white font-medium group-hover:text-[#22D3EE] transition-colors">{CONTACT.email}</p></div>
              </a>
              <a href={`tel:${CONTACT.phoneTel}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-2xl bg-[#0A84FF]/10 flex items-center justify-center"><Phone className="text-[#0A84FF]" size={20} /></div>
                <div><p className="text-gray-500 text-sm">Telefone / WhatsApp</p><p className="text-white font-medium group-hover:text-[#0A84FF] transition-colors">{CONTACT.phoneDisplay}</p></div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center"><MapPin className="text-green-400" size={20} /></div>
                <div><p className="text-gray-500 text-sm">Localização</p><p className="text-white font-medium">{CONTACT.city}</p></div>
              </div>
            </div>
            <div className="flex gap-3 mt-10">
              {[{ i: Instagram, h: CONTACT.instagram }, { i: Facebook, h: CONTACT.facebook }, { i: Youtube, h: CONTACT.youtube }].map(({ i: Icon, h }, idx) => (
                <a key={idx} href={h} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#22D3EE] hover:border-[#22D3EE] transition-colors"><Icon size={18} /></a>
              ))}
            </div>
          </motion.div>

          <motion.form initial={{ opacity: 0, x: 40 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.15 }}
            onSubmit={handleSubmit} className="card-surface rounded-3xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="name" value={formData.name} onChange={handleChange} placeholder="Seu nome *" className={inputCls} data-testid="contact-name" />
              <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Seu email *" className={inputCls} data-testid="contact-email" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Telefone / WhatsApp" className={inputCls} data-testid="contact-phone" />
              <input name="event_date" value={formData.event_date} onChange={handleChange} placeholder="Data do evento (opcional)" className={inputCls} data-testid="contact-date" />
            </div>
            <select name="contract_type" value={formData.contract_type} onChange={handleChange} className={inputCls} data-testid="contact-contract-type">
              <option value="" className="bg-[#0A1020]">Tipo de contratação</option>
              <option value="orgao_publico" className="bg-[#0A1020]">Órgão Público / Prefeitura</option>
              <option value="festa_cidade" className="bg-[#0A1020]">Festa da Cidade</option>
              <option value="festival_publico" className="bg-[#0A1020]">Festival Público</option>
              <option value="evento_corporativo" className="bg-[#0A1020]">Evento Corporativo</option>
              <option value="festa_particular" className="bg-[#0A1020]">Festa Particular</option>
            </select>
            <input name="contract_details" value={formData.contract_details} onChange={handleChange} placeholder={detailsPlaceholder()} className={inputCls} data-testid="contact-details" />
            <input name="subject" value={formData.subject} onChange={handleChange} placeholder="Assunto" className={inputCls} data-testid="contact-subject" />

            {artists.length > 0 && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Artistas de interesse (opcional)</p>
                <div className="flex flex-wrap gap-2">
                  {artists.map((a) => {
                    const on = formData.selected_artists.includes(a.name);
                    return (
                      <button type="button" key={a.id} onClick={() => toggleArtist(a.name)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-gradient-to-r from-[#0A84FF] to-[#22D3EE] text-white border-transparent" : "border-white/15 text-gray-300 hover:border-[#22D3EE]"}`}>
                        {a.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <textarea required name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Conte-nos sobre seu evento *" className={inputCls} data-testid="contact-message" />

            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="contact-submit">
              {loading ? "Enviando..." : (<>Enviar mensagem <Send size={18} /></>)}
            </button>
            {success && (
              <p className="text-center text-emerald-400 text-sm flex items-center justify-center gap-2" data-testid="contact-success"><Check size={16} />Mensagem enviada! Retornaremos em breve.</p>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// ---------------- Footer ----------------
const Footer = () => (
  <footer className="bg-[#060A14] border-t border-white/5 relative" data-testid="footer">
    <div className="fixed bottom-3 right-3" style={{ zIndex: 2147483647 }}>
      <div className="bg-green-600 text-white px-3 py-2 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium border border-green-400">
        <ShieldCheck className="h-5 w-5" />
        <div className="flex flex-col leading-tight"><span className="font-bold text-xs">SITE SEGURO</span><span className="text-[10px] opacity-90">SSL/TLS Ativo</span></div>
      </div>
    </div>

    <div className="py-10 overflow-hidden text-center"><h2 className="footer-brand">RITMO</h2></div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src={LOGO_FULL} alt="Ritmo Produções e Eventos" className="h-20 w-auto mb-4 rounded-lg" />
          <p className="text-gray-400 max-w-md">Excelência em produção e eventos. Shows, festas, estrutura e criatividade — no ritmo certo.</p>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white mb-4">Navegação</h4>
          <ul className="space-y-3">
            {[["Sobre", "#sobre"], ["Serviços", "#servicos"], ["Artistas", "#artistas"], ["Notícias", "#noticias"], ["Galeria", "#galeria"], ["Contato", "#contato"]].map(([t, h]) => (
              <li key={h}><a href={h} className="text-gray-400 hover:text-[#22D3EE] transition-colors">{t}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white mb-4">Contato</h4>
          <ul className="space-y-3 text-gray-400">
            <li className="flex items-center gap-2"><Mail size={16} className="text-[#22D3EE]" />{CONTACT.email}</li>
            <li className="flex items-center gap-2"><Phone size={16} className="text-[#0A84FF]" />{CONTACT.phoneDisplay}</li>
            <li className="flex items-start gap-2"><MapPin size={16} className="text-green-500 mt-1" /><span>{CONTACT.city}</span></li>
          </ul>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Ritmo Produções e Eventos. Todos os direitos reservados.</p>
        <div className="flex gap-4">
          {[{ i: Instagram, h: CONTACT.instagram }, { i: Facebook, h: CONTACT.facebook }, { i: Youtube, h: CONTACT.youtube }].map(({ i: Icon, h }, idx) => (
            <a key={idx} href={h} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#22D3EE] transition-colors"><Icon size={20} /></a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

// ---------------- Main Website ----------------
const MainWebsite = () => {
  const [artists, setArtists] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try { await axios.post(`${API}/seed`); } catch (e) { /* ignore */ }
      axios.get(`${API}/artists`).then((r) => mounted && setArtists(r.data)).catch(() => {});
      axios.get(`${API}/news`).then((r) => mounted && setNews(r.data)).catch(() => {});
      axios.get(`${API}/gallery`).then((r) => mounted && setGallery(r.data)).catch(() => {});
    };
    init();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="bg-[#060A14] min-h-screen" data-testid="app-container">
      <Navigation />
      <HeroSection />
      <MarqueeSection />
      <AboutSection />
      <ServicesSection />
      <ProcessSection />
      <ArtistsSection artists={artists} />
      <NewsSection news={news} />
      <GallerySection gallery={gallery} />
      <ContactSection />
      <Footer />
    </div>
  );
};

// ---------------- Under Construction (Coming Soon) ----------------
const ConstructionPage = () => {
  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#060A14] text-center px-6" data-testid="construction-page">
      {/* Aurora background */}
      <div className="aurora w-[520px] h-[520px] bg-[#0A84FF]/40 -top-24 -left-16" />
      <div className="aurora w-[460px] h-[460px] bg-[#22D3EE]/30 bottom-0 right-0 animate-float" />
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#060A14]/30 to-[#060A14]" />

      <div className="relative z-10 flex flex-col items-center">
        {/* Logo with pulse rings */}
        <div className="relative flex items-center justify-center mb-10">
          <span className="absolute w-64 h-64 rounded-full border border-[#22D3EE]/20 animate-spin-slow" />
          <span className="absolute w-72 h-72 rounded-full bg-[#0A84FF]/20 blur-3xl" />
          <motion.img
            src={LOGO_ROUND}
            alt="Ritmo Produções e Eventos"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="relative w-52 h-52 sm:w-60 sm:h-60 rounded-full object-cover ring-1 ring-white/10 animate-float shadow-2xl"
          />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-7">
          <span className="w-2 h-2 rounded-full bg-[#22D3EE] animate-pulse" />
          <span className="text-xs font-medium tracking-[0.35em] uppercase text-gray-300">Produções &amp; Eventos • Bahia</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
          className="font-display font-bold text-4xl sm:text-6xl uppercase leading-[0.95]">
          Estamos no <span className="gradient-text">ritmo</span><br />da construção
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }}
          className="text-gray-300 text-lg max-w-xl mt-6 leading-relaxed">
          Nosso novo site está sendo preparado com muito cuidado. Em breve, uma experiência completa
          de shows, artistas e eventos. Fique ligado!
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
          className="flex items-center gap-3 mt-10">
          <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer"
            className="w-12 h-12 rounded-full border border-white/12 flex items-center justify-center text-gray-300 hover:text-[#22D3EE] hover:border-[#22D3EE] transition-colors" aria-label="Instagram">
            <Instagram size={20} />
          </a>
          <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer"
            className="w-12 h-12 rounded-full border border-white/12 flex items-center justify-center text-gray-300 hover:text-[#22D3EE] hover:border-[#22D3EE] transition-colors" aria-label="Facebook">
            <Facebook size={20} />
          </a>
          <a href={`mailto:${CONTACT.email}`}
            className="w-12 h-12 rounded-full border border-white/12 flex items-center justify-center text-gray-300 hover:text-[#22D3EE] hover:border-[#22D3EE] transition-colors" aria-label="Email">
            <Mail size={20} />
          </a>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-600 text-xs tracking-widest uppercase">
        © {new Date().getFullYear()} Ritmo Produções e Eventos
      </div>
    </div>
  );
};

// ---------------- App ----------------
function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Página pública: Em Construção */}
        <Route path="/" element={<ConstructionPage />} />
        {/* Site completo (oculto — para revisão interna e ativação futura) */}
        <Route path="/site" element={<MainWebsite />} />
        <Route path="/admin" element={<LoginPage />} />
        <Route path="/admin/*" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="artistas" element={<ArtistsPage />} />
          <Route path="noticias" element={<NewsPage />} />
          <Route path="galeria" element={<GalleryPage />} />
          <Route path="contatos" element={<ContactsPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
